<template>
  <v-container
    fluid
    class="my-patches">
    <!-- META-DATA -->
    <vue-headful
      :title="'My Patches' + this.$store.state.app.meta.titleSuffix"
      :description="null" />

    <beta-notice />

    <h2 v-if="acceptedSubmissions && acceptedSubmissions.length !== 0">
      Permanent Catalog
    </h2>

    <!-- ==== ACCEPTED SUBMISSIONS GRID ==== -->
    <v-item-group
      multiple
      class="patch-grid">
      <div
        v-for="patch in acceptedSubmissions"
        :key="patch.imageId"
        class="patch">
        <v-item>
          <v-img
            slot-scope="{ active, toggle }"
            :src="imageDir + patch.imageId + '.png'"
            aspect-ratio="2"
            class="patch__image"
            @click="toggle">
            <v-slide-y-transition>
              <div
                v-if="active"
                class="patch__content">
                <h2 class="patch__name">{{ patch.name }}</h2>

                <div class="patch__actions">
                  <v-btn
                    flat
                    icon
                    dark
                    color="secondary"
                    class="patch__action-button"
                    :to="{
                      name: 'patch-product',
                      params: {
                        patchGroup: 'catalog',
                        id: patch.imageId,
                      },
                    }">
                    <v-icon>link</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-slide-y-transition>
          </v-img>
        </v-item>
      </div>
    </v-item-group>

    <h2 v-if="submissions">Submitted Designs</h2>
    <!-- == no submissions == -->
    <v-alert
      outline
      :value="!loading && submissions.length === 0"
      color="accent"
      icon="warning">
      You have not submitted any designs to MemerHats.<br>
      <router-link :to="{ name: 'submit-start' }">You can check out our submission program here.</router-link>
    </v-alert>

    <!-- ==== SUBMITTED PATCH GRID ==== -->
    <v-item-group
      multiple
      class="patch-grid">
      <div
        v-for="patch in submissions"
        :key="patch.imageId"
        class="patch">
        <v-item>
          <v-img
            slot-scope="{ active, toggle }"
            :src="customsDir + patch.imageId + '.png'"
            aspect-ratio="2"
            class="patch__image"
            @click="toggle">
            <v-slide-y-transition>
              <div
                v-if="active"
                class="patch__content">
                <h2 class="patch__name">{{ patch.name }}</h2>
                <span class="patch__quantity-sold">
                  {{ patch.quantitySold }}/{{ salesThreshold }} sold
                </span>
                <span>{{ daysRemaining(patch.dateSubmitted) }} days remaining</span>
                <div class="patch__actions">
                  <v-btn
                    flat
                    icon
                    dark
                    color="secondary"
                    class="patch__action-button"
                    :to="{
                      name: 'patch-product',
                      params: {
                        patchGroup: 'submissions',
                        id: patch.imageId,
                      },
                    }">
                    <v-icon>link</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-slide-y-transition>
          </v-img>
        </v-item>
      </div>
    </v-item-group>


    <h2>Custom Patches</h2>
    <!-- == no saved customs == -->
    <v-alert
      outline
      :value="!loading && customs.length === 0"
      color="accent"
      icon="warning">
      You have not purchased any custom patches.<br>
      <router-link :to="{ name: 'custom-start' }">You can create your own custom patches here.</router-link>
    </v-alert>

    <!-- ==== CUSTOMS PATCH GRID ==== -->
    <v-item-group
      multiple
      class="patch-grid">
      <div
        v-for="patch in customs"
        :key="patch.imageId"
        class="patch">
        <v-item>
          <v-img
            slot-scope="{ active, toggle }"
            :src="customsDir + patch.imageId + '.png'"
            aspect-ratio="2"
            class="patch__image"
            @click="toggle">
            <v-slide-y-transition>
              <div
                v-if="active"
                class="patch__content">
                <h2 class="patch__name">Custom Patch</h2>
                <span>Created: {{ moment(patch.dateCreated).format('MMM Do, YYYY') }}</span>

                <div class="patch__actions">
                  <v-btn
                    flat
                    small
                    dark
                    color="secondary"
                    class="patch__action-button"
                    @click="addToCart(patch)">
                    add to cart
                    <v-icon right>add_shopping_cart</v-icon>
                  </v-btn>
                  <v-btn
                    flat
                    small
                    dark
                    color="primary"
                    class="patch__action-button"
                    @click="openSubmitForm(patch)">
                    submit design
                    <v-icon right>publish</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-slide-y-transition>
          </v-img>
        </v-item>
      </div>
    </v-item-group>

    <!-- ==== SUBMIT PATCH MODAL ==== -->
    <v-dialog
      v-model="submitModal"
      persistent
      width="480">
      <v-container class="submit">
        <div class="submit__header">
          <span>Submit Design</span>
          <v-btn
            flat
            icon
            class="submit__close-button"
            @click="submitModal = false">
            <v-icon>close</v-icon>
          </v-btn>
        </div>

        <!-- === PSEUDO-STEPPER === -->
        <!-- == Form == -->
        <section
          v-show="submitStage==='form'"
          class="submit-stage">
          <v-img
            v-if="formData.imageId"
            :src="customsDir + formData.imageId + '.png'"
            aspect-ratio="2"
            class="patch__image" />

          <v-alert
            :value="submissions.length === 0"
            type="info"
            outline>
            If this is your first time submitting a design, please read about our submission program <router-link :to="{ name: 'submit-start' }">here</router-link>.
          </v-alert>

          <v-form
            ref="submitForm"
            v-model="submitFormValid"
            class="submit-form">
            <v-text-field
              ref="name"
              v-model="formData.name"
              label="Name/Title"
              class="submit-form__name"
              :rules="[
                v => !!v || 'This field is required',
              ]"
              required />

            <v-text-field
              ref="alias"
              v-model="formData.alias"
              label="Alias"
              class="submit-form__alias"
              :rules="[
                v => !!v || 'This field is required',
                v => validateAlias(v) || 'This alias is already taken',
              ]"
              validate-on-blur
              required>
              <v-tooltip
                slot="append-outer"
                bottom>
                <v-icon slot="activator">help_outline</v-icon>
                Your alias is displayed below any patches you submit. Changing your alias here will change it on all of your existing patches.
              </v-tooltip>
            </v-text-field>

            <v-textarea
              ref="description"
              v-model="formData.description"
              label="Description"
              rows="4"
              :rules="[
                v => !!v || 'This field is required',
              ]"
              class="submit-form__description" />

            <v-combobox
              v-model="formData.suggestedTags"
              :items="sortedTags"
              label="Suggest tags"
              hint="Select tags, or create your own"
              prepend-inner-icon="local_offer"
              :counter="tagLimit"
              multiple
              persistent-hint
              class="tag-select">
              <template
                slot="selection"
                slot-scope="{ item, parent }">
                <div
                  v-ripple
                  class="tag-select__selected-tag"
                  @click="parent.selectItem(item)">
                  {{ item }}
                  <v-icon
                    small
                    class="tag-select__selected-tag-remove">
                    close
                  </v-icon>
                </div>
              </template>
            </v-combobox>

            <v-checkbox
              v-model="formData.agreeToTerms"
              color="primary"
              :rules="[
                v => !!v || 'This field is required',
              ]">
              <template v-slot:label>
                <span class="submit__terms">I agree to the <router-link :to="{ name: 'terms-and-conditions' }">Terms and Conditions</router-link> and the <router-link :to="{ name: 'artist-agreement' }">Artist Agreement</router-link>.</span>
              </template>
            </v-checkbox>
          </v-form>

          <div class="submit__action-row">
            <v-btn
              flat
              @click="submitModal = false">
              cancel
            </v-btn>
            <v-btn
              color="primary"
              :disabled="!submitFormValid"
              class="submit__button"
              @click="submitStage='preview'">
              next
            </v-btn>
          </div>
        </section>
        <!-- == Preview == -->
        <section
          v-show="submitStage==='preview'"
          class="submit-stage">
          <div class="preview">
            <v-img
              v-if="formData.imageId"
              :src="customsDir + formData.imageId + '.png'"
              aspect-ratio="2"
              class="patch__image" />
            <h2 class="preview__name">
              {{ formData.name }}
            </h2>
            <div class="preview__creator-row">
              <span>
                Created By: {{ formData.alias }}
              </span>
              <span>
                Created On: {{ moment().format('MMM Do, YYYY') }}
              </span>
            </div>
            <v-divider />
            <div
              v-if="formData.description"
              class="preview__description">
              {{ formData.description }}
            </div>
            <v-divider />
            <div class="preview__tags">
              <span
                v-for="tag in formData.suggestedTags"
                :key="tag"
                class="preview__tag">
                {{ tag }}
              </span>
              <span
                v-if="formData.suggestedTags.length===0"
                class="preview__no-tags">
                No Suggested Tags
              </span>
            </div>
          </div>

          <div class="submit-info">
            <h2 class="submit-info__header">all set</h2>
            <p class="submit-info__body-text">
              If everything looks correct, your design is ready to submit!
            </p>
            <p class="submit-info__body-text">
              After clicking 'submit' you will be taken to your patch's listing page; it is important that you save the URL for your patch's page somewhere, as that URL will be the only way to access your patch.
            </p>
            <v-divider />
            <div class="submit__action-row">
              <v-btn
                flat
                @click="submitModal = false">
                cancel
              </v-btn>
              <v-btn
                color="primary"
                :disabled="!submitFormValid"
                class="submit__button"
                @click="submitDesign()">
                submit
              </v-btn>
            </div>
          </div>
        </section>
        <!-- == Submitting == -->
        <section
          v-show="submitStage==='submitting'"
          class="submit-stage submitting">
          <h3>
            submitting...
          </h3>
          <v-progress-circular
            indeterminate
            size="100"
            width="7"
            value="true"
            class="submitting__loader" />
        </section>
        <!-- == Success == -->
        <section
          v-show="submitStage==='success'"
          class="submit-stage submitting">
          <h3
            class="submitting__header">
            success
          </h3>
          <v-icon class="submitting__icon">check_circle</v-icon>
          <v-fade-transition>
            <span
              v-if="redirectCountdown !== 0"
              class="submitting__redirect-countdown">
              redirecting in {{ redirectCountdown }}
            </span>
          </v-fade-transition>
        </section>
        <!-- == Fail == -->
        <section
          v-show="submitStage==='fail'"
          class="submit-stage submitting">
          <h3>
            Submission Failed:
          </h3>
          <v-icon class="submitting__icon">error</v-icon>
          <span>A server error occurred and your patch was not able to be submitted - please try again later.</span>
          <br>
          <span>This feature is currently in beta testing, which means we are still working out the kinks. Consider submitting a bug report: reported bugs will be fixed sooner. </span>
        </section>
      </v-container>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';

import api from '../../utils/api';

import BetaNotice from '../../components/BetaNotice.vue';

export default {
  name: 'MyPatches',
  components: {
    BetaNotice,
  },
  data() {
    return {
      submitModal: false,
      submitFormValid: false,
      tagLimit: 10,

      formData: {
        imageId: '',
        name: '',
        alias: '',
        description: '',
        suggestedTags: [],
      },
      submitStage: 'form',
      redirectCountdown: 0,
    };
  },
  computed: {
    ...mapState('user', {
      loading: 'loading',
      currentUser: 'currentUser',
      customs: 'customs',
      submissions: 'submissions',
      acceptedSubmissions: 'acceptedSubmissions',
    }),
    ...mapState('patches', {
      tags: 'tags',
      imageDir: 'imageDir',
      customsDir: 'customsDir',
      loading: 'loading',
      errorMessage: 'errorMessage',
      salesThreshold: 'salesThreshold',
      salesPeriod: 'salesPeriod',
    }),
    sortedTags() {
      return this.tags.map(x => x).sort();
    },
    tagCount() {
      return this.formData.suggestedTags.length;
    },
  },
  watch: {
    tagCount(val) {
      if (val > this.tagLimit) {
        this.$nextTick(() => this.formData.suggestedTags.pop());
      }
    },
  },
  created() {
    this.$store.dispatch('user/getCustomerCustoms', this.currentUser.publicId);
    this.$store.dispatch('user/getCustomerSubmissions', this.currentUser.publicId);
    this.$store.dispatch('user/getCustomerAcceptedSubmissions', this.currentUser.publicId);
    this.$store.dispatch('patches/getSubmissionsConfig');
    this.$store.dispatch('patches/getTags');
  },
  methods: {
    clearError() { this.errorMessage = ''; },
    daysRemaining(submitDate) {
      const endDate = this.moment(submitDate).add(this.salesPeriod, 'days');
      // -1 to not include start day in countdown
      const daysRemaining = endDate.diff(submitDate, 'days') - 1;
      return daysRemaining;
    },

    addToCart(patch) {
      const patchGroup = 'savedCustoms';
      this.$store.dispatch('cart/addPatchToCart', { patch, patchGroup });
      this.$store.dispatch('app/openMenu', 'cartDrawer');
    },

    openSubmitForm(patch) {
      this.submitModal = true;
      this.submitStage = 'form';
      this.setFormData(patch);
    },
    setFormData(patch) {
      this.$refs.submitForm.reset();
      this.$nextTick(() => {
        this.formData.imageId = patch.imageId;
        this.formData.alias = this.currentUser.alias;
      });
    },
    validateAlias() {
      // check if alias is taken
      const formAlias = this.formData.alias;
      if (formAlias !== '' && formAlias !== this.currentUser.alias) {
        api
          .getData(`/customers/alias/${formAlias}`)
          .then(() => true)
          .catch(() => false);
      }
      return true;
    },
    updateAlias() {
      const accountDetails = {
        emailAddress: this.currentUser.email,
        alias: this.formData.alias,
      };
      const shippingAddress = Object.assign({}, this.currentUser.shippingAddress);
      this.$store
        .dispatch('user/updateUserProfile', {
          accountDetails,
          shippingAddress,
        })
        .then(() => {
          //
        });
    },

    submitDesign() {
      this.submitStage = 'submitting'; // start loader
      if (this.currentUser.alias !== this.formData.alias) {
        this.updateAlias();
      }
      // construct request data object
      const { imageId } = this.formData;
      const requestData = {
        formData: {
          publicId: this.currentUser.publicId,
          name: this.formData.name,
          description: this.formData.description,
          suggestedTags: this.formData.suggestedTags,
        },
      };

      // send data to server
      api
        .putData(`/customs/${imageId}`, requestData)
        .then((res) => {
          this.submitStage = 'success';
          // refresh data on page
          this.$store.dispatch('user/getCustomerCustoms', this.currentUser.publicId);
          this.$store.dispatch('user/getCustomerSubmissions', this.currentUser.publicId);
          this.redirectCountdown = 5;

          // COUNTDOWN
          const countdown = setInterval(() => {
            if (this.redirectCountdown > 1) {
              this.redirectCountdown--;
            } else {
              // REDIRECT
              clearInterval(countdown);
              // if they haven't close the modal, redirect
              if (this.submitModal === true) {
                this.$router.push(`/patches/submissions/${res.data.imageId}`);
              }
            }
          }, 1000);
        })
        .catch(() => {
          this.submitStage = 'fail';
          setTimeout(() => {
            this.submitModal = false;
            this.submitStage = 'form';
            this.$refs.submitForm.reset();
          }, 3000);
        });
    },
  },
};
</script>

<style lang="stylus" scoped>
@require('../../stylus/base')

.my-patches
  @media screen and (max-width: 48rem)
    padding-right: 0
    padding-left: 0

.patch-grid
  display: flex;
  flex-flow: row wrap
  align-items: flex-start
  justify-content: flex-start
  width: 100%
  @media screen and (max-width: 48rem)
    margin-right: $base-padding * -1
    margin-left: $base-padding * -1

.patch
  position: relative
  flex-basis: 100%
  flex-grow: 0
  flex-shrink: 1
  @media screen and (min-width: 32rem)
    flex-basis: 50%
  @media screen and (min-width: 120rem)
    flex-basis: 25%

  &__placeholder
    display: flex
    align-items: center
    justify-content: center
    height: 100%
    width: 100%

  &__content
    display: flex
    flex-flow: column nowrap
    justify-content: space-between
    height: 100%
    padding: $base-padding*.5 $base-padding
    font-size: $font-size--small
    color: $secondary
    // text-shadow: $base-text-shadow
    background-color: rgba(6, 9, 15, .8)

  &__name
    position: relative
    width: 100%
    line-height: normal
    white-space: nowrap
    text-overflow: ellipsis
    overflow: hidden
    @media screen and (max-width: 24rem)
      font-size: 1rem
    @media screen and (max-width: 60rem)
      font-size: 1.25rem

  &__actions
    display: flex
    flex-flow: row wrap
    align-items: center
    justify-content: flex-end
    padding-bottom: $base-padding*.5
  &__action-button
    margin: 0

.submit
  position: relative
  background-color: $white
  padding: $base-padding*2

  &__header
    display: flex
    flex-flow: row nowrap
    align-items: center
    justify-content: space-between
    @extends $base-subheader
    padding-bottom: $base-padding
  &__close-button
    margin: 0

  &__action-row
    display: flex
    flex-flow: row wrap
    justify-content: flex-end

  &__terms
    font-size: $font-size--small


.tag-select
  &__selected-tag
    position: relative
    display: flex
    flex-flow: row nowrap
    align-items: center
    justify-content: space-between
    margin-right: $base-padding
    padding: $base-padding*.25 $base-padding
    font-size: .9rem
    text-transform: capitalize
    background-color: $accent--light
    cursor: pointer
    &::before
      content: ''
      position: absolute
      top: 0
      left: 0
      height: 100%
      width: 100%
      z-index: 1
      background-color: $black
      opacity: 0
      transition: $base-transition
      cursor: pointer
    &:hover::before
      opacity: .12
  &__selected-tag-remove
    margin-left: $base-padding

.preview
  position: relative
  display: flex
  flex-flow: column nowrap
  flex: 1 1 0
  align-items: stretch
  justify-content: flex-start

  padding: $base-padding
  padding-bottom: 0
  box-shadow: $base-box-shadow
  @media screen and (max-width: 36rem)
    flex-basis: 100%

  &__name
    font-size: 1.75rem
  &__creator-row
    display: flex
    flex-wrap: wrap
    justify-content: space-between
    font-size: $font-size--small
    color: $accent--dark
  &__description
    padding-top: $base-padding
    padding-bottom: $base-padding
  &__tags
    display: flex
    flex-flow: row wrap
    padding: $base-padding 0 0 0
  &__tag
    position: relative
    display: flex
    flex-flow: row nowrap
    align-items: center
    justify-content: space-between
    margin-right: $base-padding
    margin-bottom: $base-padding
    padding: $base-padding*.25 $base-padding
    font-size: .9rem
    text-transform: capitalize
    background-color: $accent--light
  &__no-tags
    padding-bottom: $base-padding
    font-size: $font-size--small
    font-style: italic
    text-transform: uppercase
    color: $accent--dark

.submit-info
  flex: 1 1 0
  padding-left: $base-padding
  @media screen and (max-width: 26rem)
    flex-basis: 100%
    padding-left: 0
    padding-top: $base-padding*2
  &__header
    font-size: 1.75rem

.submitting
  display: flex
  flex-flow: column nowrap
  align-items: center
  justify-content: center
  padding: $base-padding*2
  color: $primary
  border-radius: $base-border-radius
  transition: $base-transition
  &__header
    font-size: 1.5rem
    font-weight: $base-header-font-weight
    text-align: center
  &__loader
    margin: $base-padding*4
  &__icon
    color: inherit
    font-size: 8rem
  &__redirect-countdown
    padding: $base-padding $base-padding*2
    text-transform: capitalize
    color: $secondary
    background-color: $primary
    border-radius: $base-border-radius

</style>
